import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { Card, Button, Badge, EmptyState, PageHeader, Skeleton, ScoreBadge } from '../components/ui.jsx';
import { ClipboardList, Clock, Play, BarChart3 } from 'lucide-react';

export default function AssessmentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['assessments-me'],
    queryFn: api.listMyAssessments
  });
  const { data: modules } = useQuery({ queryKey: ['modules'], queryFn: api.listModules });

  const startMutation = useMutation({
    mutationFn: (module_id) => api.startAssessment({ module_id }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['assessments-me'] });
      navigate(`/assessments/${res.session_id}/take`);
    }
  });

  if (isLoading) return <SkeletonGrid />;

  const sessions = data?.sessions || [];
  const assignments = data?.assignments || [];
  const inProgress = sessions.filter(s => s.status === 'in_progress');
  const completed = sessions.filter(s => s.status === 'completed');
  const completedModuleIds = new Set(sessions.filter(s => s.status === 'completed').map(s => s.module_id));

  return (
    <>
      <PageHeader
        title="My assessments"
        description="Take new assessments, resume in-progress ones, and review completed reports." />

      {inProgress.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-ink-700 mb-3">In progress</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {inProgress.map(s => (
              <Card key={s.id}>
                <Badge variant="amber" className="mb-2">In progress</Badge>
                <h3 className="text-base font-semibold text-ink-900 mb-1">{s.module_name}</h3>
                <p className="text-xs text-ink-500 mb-4">~{s.estimated_minutes} minutes</p>
                <Button onClick={() => navigate(`/assessments/${s.id}/take`)} className="w-full"><Play className="w-4 h-4 mr-1.5" />Resume</Button>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {assignments.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-ink-700 mb-3">Assigned to you</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignments.map(a => (
              <Card key={a.id}>
                <Badge variant="blue" className="mb-2">Assigned</Badge>
                <h3 className="text-base font-semibold text-ink-900 mb-1">{a.module_name}</h3>
                <p className="text-xs text-ink-500 mb-4 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> ~{a.estimated_minutes} minutes
                  {a.due_at ? <span className="ml-2">· Due {new Date(a.due_at).toLocaleDateString('en-AU')}</span> : null}
                </p>
                <Button loading={startMutation.isPending && startMutation.variables === a.module_id}
                  onClick={() => startMutation.mutate(a.module_id)} className="w-full">
                  <Play className="w-4 h-4 mr-1.5" />Start assessment
                </Button>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {completed.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-ink-700 mb-3">Completed</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {completed.map(s => (
              <Card key={s.id}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <Badge variant="green" className="mb-2">Completed</Badge>
                    <h3 className="text-base font-semibold text-ink-900">{s.module_name}</h3>
                    <p className="text-xs text-ink-500">{new Date(s.completed_at).toLocaleDateString('en-AU')}</p>
                  </div>
                </div>
                <Button variant="secondary" onClick={() => navigate(`/results/${s.id}`)} className="w-full">
                  <BarChart3 className="w-4 h-4 mr-1.5" />View results
                </Button>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {modules?.modules?.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-ink-700 mb-3">Available modules</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.modules.filter(m => !completedModuleIds.has(m.id) && !inProgress.some(s => s.module_id === m.id) && !assignments.some(a => a.module_id === m.id)).slice(0, 9).map(m => (
              <Card key={m.id}>
                <div className="flex items-start justify-between mb-2">
                  <Badge variant={m.category === 'generic' ? 'purple' : 'grey'}>{m.category === 'generic' ? 'Generic' : m.department_type}</Badge>
                </div>
                <h3 className="text-base font-semibold text-ink-900 mb-1">{m.name}</h3>
                <p className="text-xs text-ink-500 mb-2 line-clamp-2">{m.description}</p>
                <p className="text-xs text-ink-400 mb-3">~{m.estimated_minutes} min · {m.question_count} questions</p>
                <Button variant="secondary" onClick={() => startMutation.mutate(m.id)}
                  loading={startMutation.isPending && startMutation.variables === m.id} className="w-full">Start now</Button>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {sessions.length === 0 && assignments.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No assessments yet"
          description="Start with Core Workplace Capabilities — a 15-minute baseline that helps you understand your strengths."
          action={modules?.modules?.[0] ? (
            <Button onClick={() => startMutation.mutate(modules.modules[0].id)} loading={startMutation.isPending}>Start your first assessment</Button>
          ) : null} />
      ) : null}
    </>
  );
}

function SkeletonGrid() {
  return (
    <>
      <div className="mb-8"><Skeleton className="h-8 w-48 mb-2" /><Skeleton className="h-4 w-96" /></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-44" />)}
      </div>
    </>
  );
}
